import Seo from "@/components/Seo";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite, siteText } from "@/context/SiteContext";
import { toImageUrl, handleImgError, getDriveId, toStreamUrl } from "@/lib/driveUrl";

function toEmbedUrl(url) {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  return url; // vidéo Google Drive : lien /preview déjà intégrable
}

/** Tuile vidéo de la mosaïque : lecteur natif (fiable sur mobile) pour Google Drive et fichiers directs,
 *  iframe seulement pour YouTube. Le lecteur d'aperçu Drive affichait une image noire sur téléphone.
 *  Repli : si le navigateur ne sait pas décoder la vidéo (ex. HEVC iPhone), on bascule sur le lecteur Drive qui la convertit. */
function VideoTile({ item, index, span }) {
  const driveId = getDriveId(item.url);
  const isDirectFile = /\.(mp4|webm|mov)(\?|#|$)/i.test(item.url);
  const [failed, setFailed] = useState(false);
  const native = (driveId || isDirectFile) && !failed;
  return (
    <div data-testid={`canvas-video-${index}`} className={`relative overflow-hidden rounded-xl bg-black shadow ${span}`}>
      {native ? (
        <video
          src={toStreamUrl(item.url)}
          poster={driveId ? `https://drive.google.com/thumbnail?id=${driveId}&sz=w800` : undefined}
          controls
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-contain"
        />
      ) : (
        <iframe
          src={driveId ? `https://drive.google.com/file/d/${driveId}/preview` : toEmbedUrl(item.url)}
          title={item.title || "Vidéo"}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      )}
      {item.title && (
        <div className={`pointer-events-none absolute inset-0 flex p-4 ${native ? "items-start bg-gradient-to-b from-black/70 to-transparent" : "items-end bg-gradient-to-t from-black/70 to-transparent"}`}>
          <span className="text-xs font-bold text-white">{item.title}</span>
        </div>
      )}
    </div>
  );
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Rythme de la mosaïque : grandes, hautes, larges et petites cases alternées
const SPAN_PATTERN = [
  "col-span-2 row-span-2",
  "",
  "row-span-2",
  "",
  "",
  "col-span-2",
  "",
  "row-span-2",
  "",
  "",
];

const MAX_ITEMS = 50;

export default function Mediatheque() {
  const [items, setItems] = useState([]);
  const [albumItems, setAlbumItems] = useState([]);
  const { settings } = useSite();

  useEffect(() => {
    api.get("/media").then((r) => setItems(r.data)).catch(() => {});
    api.get("/album").then((r) => setAlbumItems(r.data.items || [])).catch(() => {});
  }, []);

  // Au plus 50 photos/vidéos : mélangées au hasard à chaque visite (défaut),
  // ou les 50 plus récentes si la case de l'admin est décochée.
  const canvas = useMemo(() => {
    const media = items
      .filter((i) => i.kind !== "audio")
      .map((i) => ({ key: i.id, type: i.kind, url: i.url, title: i.title, stamp: i.created_at || "" }));
    const album = albumItems.map((a, idx) => ({ key: `album-${idx}`, type: a.kind, url: a.url, title: "", name: a.name || "" }));
    let all;
    if (settings?.gallery_random === false) {
      // Les plus récentes : médiathèque par date d'ajout, dossier Drive par nom de fichier décroissant
      all = [
        ...[...media].sort((a, b) => b.stamp.localeCompare(a.stamp)),
        ...[...album].sort((a, b) => b.name.localeCompare(a.name)),
      ];
    } else {
      all = shuffle([...media, ...album]);
    }
    return all.slice(0, MAX_ITEMS);
  }, [items, albumItems, settings?.gallery_random]);

  return (
    <div>
      <Seo title="Galerie photos et vidéos" description="Photos et vidéos des bals et concerts des Sonneurs de la Côte : l'ambiance du bal trad comme si vous y étiez." path="/galerie" />
      <section className="border-b border-stone-200 bg-[#1E1B18] py-16 text-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <span className="text-xs font-bold uppercase tracking-widest text-amber-200">{siteText(settings, "page_galerie_label", "Souvenirs de bal")}</span>
          <h1 className="mt-2 font-display text-4xl font-bold uppercase tracking-tight sm:text-6xl">{siteText(settings, "page_galerie_title", "Galerie")}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-stone-300 sm:text-base">
            {siteText(settings, "page_galerie_intro", "Photos et vidéos de nos bals, scènes et stages — l'ambiance des Sonneurs comme si vous y étiez.")}
          </p>
        </div>
      </section>

      <section className="bg-[#FAF6F0] py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-8 flex justify-end">
            <Link
              to="/audio"
              data-testid="gallery-audio-link"
              className="inline-flex items-center gap-2 rounded bg-[#B83227] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow transition-colors duration-300 hover:bg-[#96281E]"
            >
              {siteText(settings, "galerie_audio_link", "Écouter nos enregistrements →")}
            </Link>
          </div>
          {canvas.length === 0 ? (
            <p className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
              Aucune photo ni vidéo pour le moment — revenez bientôt, la galerie s'enrichit régulièrement.
            </p>
          ) : (
            <div
              data-testid="gallery-canvas"
              className="grid auto-rows-[140px] grid-cols-2 gap-3 [grid-auto-flow:dense] sm:auto-rows-[165px] sm:grid-cols-3 xl:auto-rows-[185px] xl:grid-cols-4"
            >
              {canvas.map((item, i) => {
                const span = item.type === "video" ? "col-span-2 row-span-2" : SPAN_PATTERN[i % SPAN_PATTERN.length];
                return item.type === "video" ? (
                  <VideoTile key={item.key} item={item} index={i} span={span} />
                ) : (
                  <figure key={item.key} data-testid={`canvas-photo-${i}`} className={`group relative overflow-hidden rounded-xl shadow ${span}`}>
                    <img src={toImageUrl(item.url)} alt={item.title || "Photo du groupe"} loading="lazy" onError={handleImgError} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
                    {item.title && (
                      <figcaption className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent p-4 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                        <span className="text-xs font-bold text-white">{item.title}</span>
                      </figcaption>
                    )}
                  </figure>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
